import React, { useCallback, useEffect, useState } from 'react';
import { Card, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { APP_LOCAL_DATE_FORMAT } from '../../../config/constants';
import { listMeasuringInstrumentInventorys } from '../../measuring-instrument-inventories/services/measuring-instrument-inventoryService';
import { MeasuringInstrumentInventory } from '../../measuring-instrument-inventories/types/MeasuringInstrumentInventory';

interface InspectorMeasuringInstrumentInventoryProps {
  referenceId?: number;
}

export default function InspectorMeasuringInstrumentInventory({
  referenceId,
}: InspectorMeasuringInstrumentInventoryProps) {
  const [data, setData] = useState<MeasuringInstrumentInventory[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const fetchData = useCallback(async () => {
    if (!referenceId) {
      setData([]);
      setTotal(0);
      return;
    }
    setLoading(true);
    try {
      const response = await listMeasuringInstrumentInventorys(
        page - 1,
        pageSize,
        'id,asc',
        `accountId.equals=${referenceId}`
      );
      setData(response.content);
      setTotal(response.totalElements);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, referenceId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const columns: ColumnsType<MeasuringInstrumentInventory> = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 80 },
    {
      title: 'Ölçüm Cihazı',
      key: 'measuringInstrument',
      render: (_, row) => row?.measuringInstrument?.name ?? '-',
    },
    { title: 'Marka', dataIndex: 'brand', key: 'brand' },
    { title: 'Model', dataIndex: 'model', key: 'model' },
    { title: 'Seri Numarası', dataIndex: 'serialNumber', key: 'serialNumber' },
    {
      title: 'Kalibrasyon Tarihi',
      dataIndex: 'calibrationDate',
      key: 'calibrationDate',
      render: (value: any) => (value ? dayjs(value).format(APP_LOCAL_DATE_FORMAT) : '-'),
    },
    {
      title: 'Kalibrasyon Geçerlilik Tarihi',
      dataIndex: 'nextCalibrationDate',
      key: 'nextCalibrationDate',
      render: (value: any) => (value ? dayjs(value).format(APP_LOCAL_DATE_FORMAT) : '-'),
    },
  ];

  if (!referenceId) {
    return (
      <Card className="br-30px kontrolx-card">
        <Typography.Text type="secondary">P.K.M. bilgileri yükleniyor...</Typography.Text>
      </Card>
    );
  }

  return (
    <Card
      className="br-30px kontrolx-card"
      title={(
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Typography.Title level={4} style={{ margin: 0 }}>Ölçüm Cihazı Envanteri</Typography.Title>
          <Typography.Text type="secondary">
            <strong>({total})</strong> kayıt listelendi
          </Typography.Text>
        </div>
      )}
    >
      <Table
        rowKey="id"
        dataSource={data}
        columns={columns}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['5', '10', '25', '50'],
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize);
          },
        }}
      />
    </Card>
  );
}
